import { useQuery } from '@tv2/lib-graphql';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { getAdobeVisitorValues } from '@tv2/lib-offspring/libs/adobe/adobeVisitor';
import { NotifiersDocument } from './__generated__/Notifiers';

export type NotificationButtonAction =
  | 'CHECKOUT'
  | 'CONTENT'
  | 'LINK'
  | 'REACTIVATE'
  | 'SNOOZE'
  | 'CLOSE';

export type NotificationButton = {
  buttonColor: string;
  buttonLink: string;
  buttonText: string;
  buttonType: NotificationButtonAction;
};

export type Notification = {
  id: string;
  colorMode: string;
  description: string;
  title: string;
  logo: string | null;
  backgroundImage: string | null;
  buttons: Array<NotificationButton>;
  trackingInfo: {
    identifier: string;
    details: {};
    label: string;
  };
};

export const useNotifiers = () => {
  const pathname = usePathname() ?? '';
  const adobeValues = typeof window === 'undefined' ? undefined : getAdobeVisitorValues();
  const [notification, setNotification] = useState<Notification | null>(null);
  const { data, fetching } = useQuery({
    query: NotifiersDocument,
    headers: adobeValues
      ? {
          'client-platform-adobe-visitor': JSON.stringify(adobeValues),
        }
      : undefined,
    variables: { path: pathname },
    requestPolicy: 'cache-and-network',
    pause: notification !== null,
  });
  if (notification !== null) {
    return {
      loading: false,
      notification,
    };
  }
  if (data?.notifiers.notification) {
    const notificationData = data?.notifiers.notification;
    const new_notification: Notification = {
      id: notificationData.id,
      trackingInfo: {
        identifier: 'user interaction',
        details: {},
        label: notificationData.title,
      },
      colorMode: notificationData.colorMode,
      description: notificationData.description,
      title: notificationData.title,
      logo: notificationData.logo,
      backgroundImage: notificationData.backgroundImage,
      buttons: notificationData.buttons.map(button => {
        return {
          buttonColor: button.buttonColor,
          buttonLink: button.buttonLink,
          buttonText: button.buttonText,
          buttonType: button.buttonType,
        };
      }),
    };
    setNotification(new_notification);
  }
  return {
    loading: fetching,
    notification,
  };
};
